import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EntityAutocomplete } from "@/components/EntityAutocomplete";
import { MargenBadge, EstadoRegistroBadge, EstadoCobroBadge, EstadoPagoBadge, TipoViajeBadge, FueraTarifaBadge, COST_TYPE_LABELS } from "@/components/LoadBadges";
import { companyColorKey } from "@/lib/companyColors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Pencil, Ban, CircleDollarSign, Plus, Trash2,
  AlertTriangle, Link2, History, FileText, Save, X, Lock, LockOpen, BadgeDollarSign, Undo2, ArrowLeftRight, ExternalLink,
} from "lucide-react";

const money = (n, currency = "USD") => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`);

const ACCION_LABELS = {
  crear: "Creó", modificar: "Modificó", invalidar: "Invalidó", login: "Inició sesión", desactivar: "Desactivó",
  bloquear: "Bloqueó automáticamente", desbloquear: "Desbloqueó", aprobar_fuera_tarifa: "Aprobó fuera de tarifa",
  anular: "Anuló un costo", revertir_pago: "Revirtió un pago", revertir_cobro: "Revirtió un cobro",
};

export default function LoadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cruces, setCruces] = useState([]);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [needsCobroDobleConfirm, setNeedsCobroDobleConfirm] = useState(false);
  const [confirmCobroDoble, setConfirmCobroDoble] = useState(false);

  const [addingCost, setAddingCost] = useState(false);
  const [costForm, setCostForm] = useState(null);
  const [savingCost, setSavingCost] = useState(false);
  const [costFueraTarifaWarning, setCostFueraTarifaWarning] = useState(null);
  const [confirmCostFueraTarifa, setConfirmCostFueraTarifa] = useState(false);

  const [invalidateOpen, setInvalidateOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [generarReemplazo, setGenerarReemplazo] = useState(true);
  const [invalidating, setInvalidating] = useState(false);

  const [collectOpen, setCollectOpen] = useState(false);
  const [collectForm, setCollectForm] = useState({ fecha_cobro: "", referencia_cobro: "" });
  const [collecting, setCollecting] = useState(false);

  const [payCostId, setPayCostId] = useState(null);
  const [payForm, setPayForm] = useState({ fecha_pago: "", referencia_pago: "", confirmar_pago_duplicado: false });
  const [payWarning, setPayWarning] = useState(null);
  const [paying, setPaying] = useState(false);

  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockMotivo, setUnlockMotivo] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const [anularCostId, setAnularCostId] = useState(null);
  const [anularMotivo, setAnularMotivo] = useState("");
  const [anulando, setAnulando] = useState(false);

  const [revertPagoCostId, setRevertPagoCostId] = useState(null);
  const [revertPagoMotivo, setRevertPagoMotivo] = useState("");
  const [revertingPago, setRevertingPago] = useState(false);

  const [revertCobroOpen, setRevertCobroOpen] = useState(false);
  const [revertCobroMotivo, setRevertCobroMotivo] = useState("");
  const [revertingCobro, setRevertingCobro] = useState(false);

  const isDueno = user?.rol === "dueno";
  const bloqueado = !!load?.bloqueado;
  const canVerGanancia = can("loads", "ver_ganancia");
  const canEditar = can("loads", "editar") && load?.estado_registro !== "invalidado" && !bloqueado;
  const canInvalidar = can("loads", "invalidar") && load?.estado_registro !== "invalidado" && !bloqueado;
  const canAnularCosto = can("loads", "anular_costo") && load?.estado_registro !== "invalidado" && !bloqueado;

  const fetchLoad = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/loads/${id}`);
      setLoad(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchLoad(); }, [fetchLoad]);

  useEffect(() => {
    if (!id || !can("cruces", "ver")) return;
    api.get("/cruces", { params: { load_id: id, limit: 50 } })
      .then(({ data }) => setCruces(data.items))
      .catch(() => {});
  }, [id, can]);

  const startEdit = () => {
    setEditForm({
      referencia_cliente: load.referencia_cliente,
      origen_ciudad: load.origen_ciudad, origen_estado: load.origen_estado, origen_pais: load.origen_pais,
      destino_ciudad: load.destino_ciudad, destino_estado: load.destino_estado, destino_pais: load.destino_pais,
      fecha_pickup: load.fecha_pickup, fecha_delivery: load.fecha_delivery,
      carrier_id: load.carrier_id, carrier_label: load.carrier_nombre ? `${load.carrier_codigo} — ${load.carrier_nombre}` : "",
      monto_cobro: load.monto_cobro, moneda_cobro: load.moneda_cobro,
      unidad: load.unidad || "", numero_factura: load.numero_factura || "", operador: load.operador || "",
      placas: load.placas || "", notas: load.notas || "",
    });
    setNeedsCobroDobleConfirm(false);
    setConfirmCobroDoble(false);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/loads/${id}`, { ...editForm, confirmar_cobro_doble: confirmCobroDoble });
      toast.success("Load actualizado");
      setEditing(false);
      fetchLoad();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "cobro_doble") {
        setNeedsCobroDobleConfirm(true);
        toast.warning(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSavingEdit(false);
    }
  };

  const openCollect = () => {
    setCollectForm({ fecha_cobro: new Date().toISOString().slice(0, 10), referencia_cobro: "" });
    setCollectOpen(true);
  };

  const doCollect = async () => {
    setCollecting(true);
    try {
      await api.post(`/loads/${id}/mark-collected`, collectForm);
      toast.success("Load marcado como cobrado");
      setCollectOpen(false);
      fetchLoad();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCollecting(false); }
  };

  const openPay = (costId) => {
    setPayCostId(costId);
    setPayForm({ fecha_pago: new Date().toISOString().slice(0, 10), referencia_pago: "", confirmar_pago_duplicado: false });
    setPayWarning(null);
  };

  const doPay = async () => {
    setPaying(true);
    try {
      await api.post(`/loads/${id}/costs/${payCostId}/mark-paid`, payForm);
      toast.success("Costo marcado como pagado");
      setPayCostId(null);
      fetchLoad();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "pago_duplicado") {
        setPayWarning(detail);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally { setPaying(false); }
  };

  const doUnlock = async () => {
    if (unlockMotivo.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
    setUnlocking(true);
    try {
      await api.post(`/loads/${id}/unlock`, { motivo: unlockMotivo });
      toast.success("Load desbloqueado");
      setUnlockOpen(false);
      setUnlockMotivo("");
      fetchLoad();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUnlocking(false); }
  };

  const doInvalidate = async () => {
    if (motivo.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
    setInvalidating(true);
    try {
      const { data } = await api.post(`/loads/${id}/invalidate`, { motivo, generar_reemplazo: generarReemplazo });
      toast.success("Load invalidado");
      setInvalidateOpen(false);
      setMotivo("");
      if (data.reemplazo) navigate(`/loads/${data.reemplazo.id}`);
      else fetchLoad();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setInvalidating(false);
    }
  };

  const openAddCost = () => {
    setCostForm({ tipo_costo: "otro", carrier_id: null, carrier_label: "", concepto: "", monto: "", moneda: "USD", estado_pago: "pendiente" });
    setCostFueraTarifaWarning(null);
    setConfirmCostFueraTarifa(false);
    setAddingCost(true);
  };

  const saveCost = async () => {
    setSavingCost(true);
    try {
      await api.post(`/loads/${id}/costs`, {
        tipo_costo: costForm.tipo_costo, carrier_id: costForm.carrier_id || null, concepto: costForm.concepto || "",
        monto: parseFloat(costForm.monto || 0), moneda: costForm.moneda, estado_pago: costForm.estado_pago,
        confirmar_fuera_tarifa: confirmCostFueraTarifa,
      });
      toast.success("Costo agregado");
      setAddingCost(false);
      fetchLoad();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "fuera_de_tarifa") {
        setCostFueraTarifaWarning(detail);
        toast.warning(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSavingCost(false);
    }
  };

  const deleteCost = async (costId) => {
    setAnularCostId(costId);
    setAnularMotivo("");
  };

  const doAnularCost = async () => {
    if (anularMotivo.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
    setAnulando(true);
    try {
      await api.post(`/loads/${id}/costs/${anularCostId}/anular`, { motivo: anularMotivo });
      toast.success("Costo anulado");
      setAnularCostId(null);
      setAnularMotivo("");
      fetchLoad();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAnulando(false); }
  };

  const doRevertirPago = async () => {
    if (revertPagoMotivo.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
    setRevertingPago(true);
    try {
      await api.post(`/loads/${id}/costs/${revertPagoCostId}/revertir-pago`, { motivo: revertPagoMotivo });
      toast.success("Pago revertido");
      setRevertPagoCostId(null);
      setRevertPagoMotivo("");
      fetchLoad();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRevertingPago(false); }
  };

  const doRevertirCobro = async () => {
    if (revertCobroMotivo.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
    setRevertingCobro(true);
    try {
      await api.post(`/loads/${id}/revertir-cobro`, { motivo: revertCobroMotivo });
      toast.success("Cobro revertido");
      setRevertCobroOpen(false);
      setRevertCobroMotivo("");
      fetchLoad();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRevertingCobro(false); }
  };

  if (loading || !load) return <div className="text-sm text-muted-foreground">Cargando...</div>;

  const invalidado = load.estado_registro === "invalidado";

  return (
    <div className="max-w-5xl">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span className={`font-mono ${invalidado ? "text-muted-foreground line-through" : ""}`} data-testid="load-detail-numero">{load.numero_load}</span>
            {/* P44: el número que tenía antes de pasar a la serie de la moneda de su factura */}
            {load.numeros_anteriores?.length > 0 && (
              <span className="font-mono text-xs font-normal text-muted-foreground" data-testid="load-detail-numeros-anteriores">
                antes {load.numeros_anteriores.join(", ")}
              </span>
            )}
            {/* P46: el número de load que lleva tráfico en su archivo (puede ser más de uno) */}
            {load.loads_trafico?.length > 0 && (
              <span className="font-mono text-xs font-normal text-muted-foreground" data-testid="load-detail-loads-trafico">
                tráfico {load.loads_trafico.join(", ")}
              </span>
            )}
            <TipoViajeBadge tipo={load.tipo_viaje} />
            <EstadoRegistroBadge estado={load.estado_registro} testId="load-detail-estado-registro" />
            {load.fuera_de_tarifa && <FueraTarifaBadge testId="load-detail-fuera-tarifa-badge" />}
          </span>
        }
        subtitle={`${load.company_nombre} — ${load.client_nombre} — ${load.referencia_cliente}`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/loads")} className="gap-2" data-testid="load-detail-back">
              <ArrowLeft className="h-4 w-4" /> Volver
            </Button>
            {!editing && canEditar && (
              <Button variant="outline" onClick={startEdit} className="gap-2" data-testid="load-detail-edit-btn">
                <Pencil className="h-4 w-4" /> Editar
              </Button>
            )}
            {canInvalidar && (
              <Button variant="outline" onClick={() => setInvalidateOpen(true)} className="gap-2 text-red-600 hover:bg-red-50" data-testid="load-detail-invalidate-btn">
                <Ban className="h-4 w-4" /> Invalidar
              </Button>
            )}
            {!invalidado && !bloqueado && can("loads", "marcar_cobrado") && load.estado_cobro !== "cobrado" && (
              <Button variant="outline" onClick={openCollect} className="gap-2" data-testid="load-detail-mark-collected">
                <CircleDollarSign className="h-4 w-4" /> Marcar cobrado
              </Button>
            )}
            {!invalidado && !bloqueado && isDueno && load.estado_cobro === "cobrado" && (
              <Button variant="outline" onClick={() => { setRevertCobroMotivo(""); setRevertCobroOpen(true); }} className="gap-2 text-amber-700 hover:bg-amber-50" data-testid="load-detail-revert-cobro-btn">
                <Undo2 className="h-4 w-4" /> Revertir cobro
              </Button>
            )}
            {bloqueado && isDueno && (
              <Button variant="outline" onClick={() => setUnlockOpen(true)} className="gap-2 text-amber-700 hover:bg-amber-50" data-testid="load-detail-unlock-btn">
                <LockOpen className="h-4 w-4" /> Desbloquear
              </Button>
            )}
          </div>
        }
      />

      {bloqueado && (
        <div className="mb-5 rounded-md border border-slate-400 bg-slate-100 p-4" data-testid="load-bloqueado-banner">
          <p className="flex items-center gap-2 font-medium text-slate-800">
            <Lock className="h-4 w-4" /> Load cerrado el {load.bloqueado_en && new Date(load.bloqueado_en).toLocaleDateString("es-MX")}. Solo el perfil de Dueño puede reabrirlo.
          </p>
        </div>
      )}
      {load.fuera_de_tarifa && (
        <div className="mb-5 rounded-md border border-orange-300 bg-orange-50 p-4" data-testid="load-fuera-tarifa-detail-banner">
          <p className="flex items-center gap-2 font-medium text-orange-800"><AlertTriangle className="h-4 w-4" /> Este load fue aprobado fuera de tarifa.</p>
          <p className="mt-1 text-sm text-orange-800">
            Margen resultante: {load.tarifa_margen_resultante}% · Margen objetivo: {load.tarifa_margen_objetivo}% · Aprobado por: {load.tarifa_aprobada_por}
          </p>
        </div>
      )}

      {!invalidado && companyColorKey(load.numero_load) === "alvarez" && (
        load.qbo_invoice ? (
          <div className="mb-5 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 p-4" data-testid="load-qbo-invoice-banner">
            <p className="text-sm text-blue-800">
              Factura QuickBooks <b>{load.qbo_invoice.qbo_doc_number}</b>
              {load.qbo_invoice.modo === "simulada" ? " (simulada)" : ""} — creada el{" "}
              {load.qbo_invoice.creada_en?.slice(0, 10)} por {load.qbo_invoice.creada_por}
            </p>
          </div>
        ) : (load.qbo_factura_externa || /\d{4,6}\s*A\b/i.test(load.numero_factura || "")) ? (
          // P41: facturado directo en QuickBooks (por fuera de la plataforma): no ofrecer facturarlo de nuevo.
          <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 p-4" data-testid="load-qbo-externa-banner">
            <p className="text-sm text-blue-800">
              Facturado directo en QuickBooks:{" "}
              {load.qbo_factura_externa ? (
                <>factura <b>#{load.qbo_factura_externa.doc}</b>{load.qbo_factura_externa.fecha ? ` del ${load.qbo_factura_externa.fecha}` : ""}
                  {load.qbo_factura_externa.cliente ? ` — ${load.qbo_factura_externa.cliente}` : ""}
                  {load.qbo_factura_externa.monto != null ? ` · $${Number(load.qbo_factura_externa.monto).toFixed(2)} USD` : ""}
                  {load.qbo_factura_externa.saldo != null ? (Number(load.qbo_factura_externa.saldo) > 0.01
                    ? ` · saldo $${Number(load.qbo_factura_externa.saldo).toFixed(2)}` : " · pagada") : ""}</>
              ) : <>N° factura <b>{load.numero_factura}</b></>}
            </p>
          </div>
        ) : can("facturacion_qbo", "crear") && (
          <div className="mb-5 flex items-center justify-between rounded-md border border-border bg-white p-4">
            <p className="text-sm text-muted-foreground">Este viaje todavía no tiene factura en QuickBooks.</p>
            <Button size="sm" variant="outline" className="gap-2" onClick={() => navigate(`/facturacion-qbo?load_id=${load.id}`)} data-testid="load-detail-facturar-qbo-btn">
              <ExternalLink className="h-4 w-4" /> Facturar en QuickBooks
            </Button>
          </div>
        )
      )}

      {invalidado && (
        <div className="mb-5 rounded-md border border-slate-300 bg-slate-100 p-4" data-testid="load-invalidated-banner">
          <p className="font-medium text-slate-700">Este load está invalidado y es de solo lectura.</p>
          <p className="mt-1 text-sm text-slate-600"><b>Motivo:</b> {load.motivo_invalidacion}</p>
          <p className="text-sm text-slate-600"><b>Por:</b> {load.invalidado_por} — {load.invalidado_en && new Date(load.invalidado_en).toLocaleString("es-MX")}</p>
          {load.reemplazado_por_numero && (
            <p className="mt-2 text-sm">
              Este load fue reemplazado por{" "}
              <Link to={`/loads/${load.reemplazado_por_load_id}`} className="font-mono font-semibold text-primary underline" data-testid="load-detail-reemplazado-por-link">
                {load.reemplazado_por_numero}
              </Link>
            </p>
          )}
          {load.tenia_movimientos_al_invalidar && load.reemplazado_por_numero && (
            <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800" data-testid="load-tenia-movimientos-banner">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Este load tenía movimientos registrados. Verificá que no se dupliquen en el reemplazo {load.reemplazado_por_numero}.
            </div>
          )}
        </div>
      )}
      {!invalidado && load.reemplaza_a_numero && (
        <div className="mb-5 rounded-md border border-primary/30 bg-green-50 p-3 text-sm" data-testid="load-reemplaza-a-banner">
          Este load reemplaza al{" "}
          <Link to={`/loads/${load.reemplaza_a_load_id}`} className="font-mono font-semibold text-primary underline" data-testid="load-detail-reemplaza-a-link">
            {load.reemplaza_a_numero}
          </Link>
          . Los cobros y pagos del original NO se copiaron: hay que registrarlos de nuevo si corresponden.
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {/* Ficha */}
          <Card data-testid="load-detail-ficha">
            <CardHeader><CardTitle className="font-heading text-base">Ficha del load</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {!editing ? (
                <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Cliente</p>
                    <p className="text-sm" data-testid="load-detail-field-cliente">{load.cliente_fuente?.nombre || load.client_nombre}</p>
                    {load.cliente_fuente?.nombre && (
                      <p className="mt-0.5 text-xs text-muted-foreground" data-testid="load-detail-cliente-fuente">
                        Como figura en {load.cliente_fuente.sistema}. En la plataforma: {load.client_nombre || "—"}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Referencia</p>
                    <p className="mt-1 text-sm font-medium" data-testid="load-detail-field-referencia">{load.referencia_cliente || "—"}</p>
                    {load.referencia_cummins_match && (
                      <p className="mt-0.5 text-xs text-primary" data-testid="load-detail-referencia-cummins">
                        Tarifa ref. Cummins ({load.referencia_cummins_match.referencia}): ${load.referencia_cummins_match.tarifa}
                      </p>
                    )}
                  </div>
                  <Field label="Carrier" value={load.carrier_nombre || "—"} />
                  <Field label="Origen" value={`${load.origen_ciudad}, ${load.origen_estado || ""} ${load.origen_pais}`} />
                  <Field label="Destino" value={`${load.destino_ciudad}, ${load.destino_estado || ""} ${load.destino_pais}`} />
                  <Field label="Pickup" value={load.fecha_pickup} mono />
                  <Field label="Delivery" value={load.fecha_delivery} mono />
                  <Field label="Unidad" value={load.unidad || "—"} />
                  <Field label="Placas" value={load.placas || "—"} mono />
                  <Field label="N° factura" value={load.numero_factura || "—"} />
                  <Field label="Operador" value={load.operador || "—"} />
                  <Field label="Carta porte (nacional)" value={load.carta_porte_nac || "—"} />
                  <Field label="Carta porte (cruce)" value={load.carta_porte_cruce || "—"} />
                  <Field label="Fecha de cruce" value={load.fecha_cruce || "—"} mono />
                  <div>
                    <p className="text-xs text-muted-foreground">Estado de cobro</p>
                    <div className="mt-1"><EstadoCobroBadge estado={load.estado_cobro_semaforo} dias={load.dias_vencimiento_cobro} testId="load-detail-estado-cobro" /></div>
                    {load.estado_cobro === "cobrado" ? (
                      <p className="mt-1 text-xs text-muted-foreground">{load.fecha_cobro} · ref. {load.referencia_cobro}</p>
                    ) : load.fecha_vencimiento_cobro ? (
                      <p className="mt-1 text-xs text-muted-foreground" data-testid="load-detail-fecha-vencimiento">
                        Vence el {load.fecha_vencimiento_cobro} — {load.origen_terminos_pago}
                        {load.fecha_delivery_estimada && (
                          <span className="ml-1 inline-flex items-center gap-0.5 font-medium text-amber-600" data-testid="load-detail-fecha-estimada-badge">
                            <AlertTriangle className="h-3 w-3" /> estimado
                          </span>
                        )}
                      </p>
                    ) : load.origen_terminos_pago ? (
                      <p className="mt-1 text-xs text-muted-foreground">{load.origen_terminos_pago}</p>
                    ) : null}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Estado de pago a carriers</p>
                    <div className="mt-1"><EstadoPagoBadge estado={load.estado_pago_carrier} testId="load-detail-estado-pago" /></div>
                  </div>
                  <Field label="Notas" value={load.notas || "—"} className="col-span-2 sm:col-span-3" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Referencia del cliente</Label>
                      <Input className="mt-1" value={editForm.referencia_cliente} onChange={(e) => setEditForm({ ...editForm, referencia_cliente: e.target.value })} data-testid="load-edit-referencia" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Carrier</Label>
                      <div className="mt-1">
                        <EntityAutocomplete type="carriers" value={editForm.carrier_id} label={editForm.carrier_label}
                          onSelect={(c) => setEditForm({ ...editForm, carrier_id: c.id, carrier_label: `${c.codigo} — ${c.razon_social}` })}
                          testId="load-edit-carrier" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input className="col-span-1" value={editForm.origen_ciudad} onChange={(e) => setEditForm({ ...editForm, origen_ciudad: e.target.value })} data-testid="load-edit-origen-ciudad" />
                    <Input value={editForm.origen_estado} onChange={(e) => setEditForm({ ...editForm, origen_estado: e.target.value })} />
                    <Select value={editForm.origen_pais} onValueChange={(v) => setEditForm({ ...editForm, origen_pais: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="MX">MX</SelectItem><SelectItem value="US">US</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input className="col-span-1" value={editForm.destino_ciudad} onChange={(e) => setEditForm({ ...editForm, destino_ciudad: e.target.value })} data-testid="load-edit-destino-ciudad" />
                    <Input value={editForm.destino_estado} onChange={(e) => setEditForm({ ...editForm, destino_estado: e.target.value })} />
                    <Select value={editForm.destino_pais} onValueChange={(v) => setEditForm({ ...editForm, destino_pais: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="MX">MX</SelectItem><SelectItem value="US">US</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Fecha pickup</Label>
                      <Input type="date" className="mt-1" value={editForm.fecha_pickup} onChange={(e) => setEditForm({ ...editForm, fecha_pickup: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Fecha delivery</Label>
                      <Input type="date" className="mt-1" value={editForm.fecha_delivery} onChange={(e) => setEditForm({ ...editForm, fecha_delivery: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Monto cobro</Label>
                      <Input type="number" className="mt-1" value={editForm.monto_cobro} onChange={(e) => setEditForm({ ...editForm, monto_cobro: e.target.value })} data-testid="load-edit-monto-cobro" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Moneda</Label>
                      <Select value={editForm.moneda_cobro} onValueChange={(v) => setEditForm({ ...editForm, moneda_cobro: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="MXN">MXN</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Notas</Label>
                    <Textarea className="mt-1" value={editForm.notas} onChange={(e) => setEditForm({ ...editForm, notas: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Unidad</Label>
                      <Input className="mt-1" value={editForm.unidad} onChange={(e) => setEditForm({ ...editForm, unidad: e.target.value })} data-testid="load-edit-unidad" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">N° factura</Label>
                      <Input className="mt-1" value={editForm.numero_factura} onChange={(e) => setEditForm({ ...editForm, numero_factura: e.target.value })} data-testid="load-edit-numero-factura" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Operador</Label>
                      <Input className="mt-1" value={editForm.operador} onChange={(e) => setEditForm({ ...editForm, operador: e.target.value })} data-testid="load-edit-operador" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Placas</Label>
                      <Input className="mt-1" value={editForm.placas} onChange={(e) => setEditForm({ ...editForm, placas: e.target.value })} placeholder="87-AD-5Z" data-testid="load-edit-placas" />
                    </div>
                  </div>
                  {needsCobroDobleConfirm && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                      <div className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle className="h-4 w-4" /> Posible doble facturación</div>
                      <label className="mt-2 flex items-center gap-2 text-sm text-amber-800">
                        <input type="checkbox" checked={confirmCobroDoble} onChange={(e) => setConfirmCobroDoble(e.target.checked)} data-testid="load-edit-confirm-cobro-doble" />
                        Confirmo que ambos cobros son correctos
                      </label>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEditing(false)} className="gap-2"><X className="h-4 w-4" /> Cancelar</Button>
                    <Button onClick={saveEdit} disabled={savingEdit || (needsCobroDobleConfirm && !confirmCobroDoble)} className="gap-2" data-testid="load-edit-save-btn">
                      <Save className="h-4 w-4" /> {savingEdit ? "Guardando..." : "Guardar"}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Costos */}
          <Card data-testid="load-detail-costos">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-base">Costos</CardTitle>
              {canEditar && (
                <Button size="sm" variant="outline" onClick={openAddCost} className="gap-1" data-testid="load-add-cost-btn">
                  <Plus className="h-3.5 w-3.5" /> Agregar costo
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Carrier / Concepto</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    {canVerGanancia && <TableHead className="text-right">USD</TableHead>}
                    <TableHead>Estado</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(load.costos || []).map((c) => (
                    <TableRow key={c.id} data-testid={`load-cost-row-${c.id}`} className={c.anulado ? "opacity-60" : ""}>
                      <TableCell className={`text-xs ${c.anulado ? "text-muted-foreground line-through" : ""}`}>{COST_TYPE_LABELS[c.tipo_costo] || c.tipo_costo}</TableCell>
                      <TableCell className={`text-xs ${c.anulado ? "text-muted-foreground line-through" : ""}`}>{c.carrier_nombre || c.concepto || "—"}</TableCell>
                      <TableCell className={`text-right font-mono text-xs ${c.anulado ? "text-muted-foreground line-through" : ""}`}>{money(c.monto, c.moneda)}</TableCell>
                      {canVerGanancia && <TableCell className={`text-right font-mono text-xs ${c.anulado ? "text-muted-foreground line-through" : ""}`}>{money(c.monto_usd)}</TableCell>}
                      <TableCell>
                        {c.anulado ? (
                          <div>
                            <Badge className="border border-slate-300 bg-slate-200 text-slate-600 hover:bg-slate-200" data-testid={`load-cost-row-${c.id}-anulado-badge`}>Anulado</Badge>
                            <p className="mt-0.5 text-[10px] text-muted-foreground">por {c.anulado_por} · {c.motivo_anulacion}</p>
                          </div>
                        ) : (
                          <>
                            <EstadoPagoBadge estado={c.estado_pago === "pagado" ? "pagado_total" : "sin_pagos"} />
                            {c.estado_pago === "pagado" && <p className="mt-0.5 text-[10px] text-muted-foreground">{c.fecha_pago} · {c.referencia_pago}</p>}
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {!bloqueado && !invalidado && !c.anulado && c.estado_pago !== "pagado" && can("loads", "marcar_pagado") && (
                            <Button size="icon" variant="ghost" onClick={() => openPay(c.id)} data-testid={`load-cost-row-${c.id}-mark-paid`}>
                              <BadgeDollarSign className="h-4 w-4 text-primary" />
                            </Button>
                          )}
                          {!bloqueado && !invalidado && c.estado_pago === "pagado" && isDueno && (
                            <Button size="icon" variant="ghost" onClick={() => { setRevertPagoCostId(c.id); setRevertPagoMotivo(""); }} title="Revertir pago" data-testid={`load-cost-row-${c.id}-revert-pago`}>
                              <Undo2 className="h-4 w-4 text-amber-700" />
                            </Button>
                          )}
                          {!c.anulado && c.estado_pago !== "pagado" && canAnularCosto && (
                            <Button size="icon" variant="ghost" onClick={() => deleteCost(c.id)} title="Anular costo" data-testid={`load-cost-row-${c.id}-anular`}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(load.costos || []).length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground">Sin costos registrados.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Tramo hermano */}
          {load.tramo_hermano && (
            <Card data-testid="load-detail-tramo-hermano">
              <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-base"><Link2 className="h-4 w-4 text-primary" /> Tramo vinculado</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div>
                    <p className="font-mono text-sm font-semibold">{load.tramo_hermano.numero_load}</p>
                    <p className="text-xs text-muted-foreground">{load.tramo_hermano.company_nombre} — {load.tramo_hermano.carrier_nombre || "sin carrier"}</p>
                    <p className="text-xs text-muted-foreground">Cobro: {money(load.tramo_hermano.monto_cobro, load.tramo_hermano.moneda_cobro)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <EstadoRegistroBadge estado={load.tramo_hermano.estado_registro} />
                    <Button size="sm" variant="outline" onClick={() => navigate(`/loads/${load.tramo_hermano.id}`)} data-testid="load-detail-open-sibling">
                      Abrir
                    </Button>
                  </div>
                </div>
                {load.advertencia_cobro_doble && (
                  <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800" data-testid="load-detail-cobro-doble-warning">
                    <AlertTriangle className="h-4 w-4" /> Ambos tramos tienen un cobro registrado al cliente. Verificar que no sea doble facturación.
                  </div>
                )}
                {canVerGanancia && load.vista_consolidada && (
                  <div className="rounded-md border border-border bg-slate-50 p-3" data-testid="load-detail-vista-consolidada">
                    <p className="mb-2 text-sm font-semibold">Vista consolidada del viaje completo</p>
                    <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                      <Field label="Cobro total" value={money(load.vista_consolidada.monto_cobro_total_usd)} mono />
                      <Field label="Costos totales" value={money(load.vista_consolidada.costos_total_usd)} mono />
                      <Field label="Ganancia neta" value={money(load.vista_consolidada.ganancia_neta_usd)} mono />
                      <div>
                        <p className="text-xs text-muted-foreground">Margen</p>
                        <MargenBadge margen_pct={load.vista_consolidada.margen_pct} margen_color={load.vista_consolidada.margen_pct >= 15 ? "verde" : load.vista_consolidada.margen_pct >= 5 ? "amarillo" : "rojo"} />
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Documentos (placeholder) */}
          <Card data-testid="load-detail-documentos">
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-base"><FileText className="h-4 w-4 text-primary" /> Documentos</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Sin documentos adjuntos todavía. Esta sección se habilita en un próximo paso (carga de POD, cartas de instrucción y facturas).</p>
            </CardContent>
          </Card>

          {/* Cruces vinculados */}
          {cruces.length > 0 && (
            <Card data-testid="load-detail-cruces">
              <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-base"><ArrowLeftRight className="h-4 w-4 text-primary" /> Cruces vinculados</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {cruces.map((c) => (
                    <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2 text-xs" data-testid={`load-detail-cruce-${c.id}`}>
                      <span className="font-mono">{c.fecha}</span>
                      <Badge className={c.sentido === "impo" ? "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50" : "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50"}>
                        {c.sentido === "impo" ? "Impo" : "Expo"}
                      </Badge>
                      <span className="truncate">{c.referencia || "—"}</span>
                      <span className="text-muted-foreground">{c.status || "—"}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-5">
          {/* Cobro y ganancia */}
          <Card data-testid="load-detail-ganancia">
            <CardHeader><CardTitle className="font-heading text-base">Cobro y ganancia</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Field label="Monto cobro" value={money(load.monto_cobro, load.moneda_cobro)} mono />
              {canVerGanancia && <Field label="Cobro en USD" value={money(load.monto_cobro_usd)} mono />}
              <Field label="Tipo de cambio usado" value={load.tipo_cambio_usado} mono />
              {canVerGanancia && (
                <>
                  <div className="border-t border-border pt-3">
                    <p className="mb-1 text-xs text-muted-foreground">Cobro − costos:</p>
                    <p className="font-mono text-xs">{money(load.monto_cobro_usd)} − {money(load.costos_total_usd)} = <b>{money(load.ganancia_neta_usd)}</b></p>
                  </div>
                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <span className="text-xs text-muted-foreground">Margen</span>
                    <MargenBadge margen_pct={load.margen_pct} margen_color={load.margen_color} testId="load-detail-margen-badge" />
                  </div>
                </>
              )}
              <div className="flex items-center justify-between border-t border-border pt-3">
                <span className="text-xs text-muted-foreground">Estado de cobro</span>
                <EstadoCobroBadge estado={load.estado_cobro_semaforo} dias={load.dias_vencimiento_cobro} />
              </div>
              {load.fecha_vencimiento_cobro && load.estado_cobro !== "cobrado" && (
                <p className="text-right text-xs text-muted-foreground" data-testid="load-detail-ganancia-vencimiento">
                  Vence el {load.fecha_vencimiento_cobro} — {load.origen_terminos_pago}
                </p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Estado de pago a carrier</span>
                <EstadoPagoBadge estado={load.estado_pago_carrier} />
              </div>
            </CardContent>
          </Card>

          {/* Historial */}
          <Card data-testid="load-detail-historial">
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-base"><History className="h-4 w-4 text-primary" /> Historial</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(load.historial || []).map((h) => (
                  <div key={h.id} className="border-l-2 border-primary/30 pl-3 text-xs" data-testid={`load-historial-entry-${h.id}`}>
                    <p className="font-medium text-foreground">{h.user_nombre} — {ACCION_LABELS[h.accion] || h.accion}</p>
                    {h.campo_modificado && (
                      <p className="text-muted-foreground">{h.campo_modificado}: {h.valor_anterior ?? "—"} → {h.valor_nuevo ?? "—"}</p>
                    )}
                    <p className="text-muted-foreground">{new Date(h.created_at).toLocaleString("es-MX")}</p>
                  </div>
                ))}
                {(load.historial || []).length === 0 && <p className="text-xs text-muted-foreground">Sin cambios registrados.</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add cost dialog */}
      <Dialog open={addingCost} onOpenChange={setAddingCost}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Agregar costo</DialogTitle></DialogHeader>
          {costForm && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Tipo de costo</Label>
                <Select value={costForm.tipo_costo} onValueChange={(v) => setCostForm({ ...costForm, tipo_costo: v })}>
                  <SelectTrigger className="mt-1" data-testid="load-cost-dialog-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(COST_TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(costForm.tipo_costo === "carrier_mx" || costForm.tipo_costo === "carrier_us") && (
                <div>
                  <Label className="text-xs text-muted-foreground">Carrier</Label>
                  <div className="mt-1">
                    <EntityAutocomplete type="carriers" paisFilter={costForm.tipo_costo === "carrier_mx" ? "MX" : "US"} value={costForm.carrier_id} label={costForm.carrier_label}
                      onSelect={(c) => setCostForm({ ...costForm, carrier_id: c.id, carrier_label: `${c.codigo} — ${c.razon_social}` })}
                      testId="load-cost-dialog-carrier" />
                  </div>
                </div>
              )}
              {["otro", "diesel", "casetas", "maniobras", "viaticos_operador", "otros_operativos"].includes(costForm.tipo_costo) && (
                <div>
                  <Label className="text-xs text-muted-foreground">Concepto</Label>
                  <Input className="mt-1" value={costForm.concepto} onChange={(e) => setCostForm({ ...costForm, concepto: e.target.value })} data-testid="load-cost-dialog-concepto" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Monto</Label>
                  <Input type="number" className="mt-1" value={costForm.monto} onChange={(e) => setCostForm({ ...costForm, monto: e.target.value })} data-testid="load-cost-dialog-monto" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Moneda</Label>
                  <Select value={costForm.moneda} onValueChange={(v) => setCostForm({ ...costForm, moneda: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="MXN">MXN</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          {costFueraTarifaWarning && (
            <div className="mt-1 rounded-md border border-orange-300 bg-orange-50 p-3" data-testid="load-cost-fuera-tarifa-warning">
              <div className="flex items-center gap-2 font-medium text-orange-800"><AlertTriangle className="h-4 w-4" /> Este costo supera el techo de la ruta.</div>
              <p className="mt-1 text-sm text-orange-800">{costFueraTarifaWarning.message}</p>
              <label className="mt-2 flex items-center gap-2 text-sm text-orange-800">
                <input type="checkbox" checked={confirmCostFueraTarifa} onChange={(e) => setConfirmCostFueraTarifa(e.target.checked)} data-testid="load-cost-confirm-fuera-tarifa" />
                Confirmo y apruebo continuar fuera de tarifa
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingCost(false)}>Cancelar</Button>
            <Button onClick={saveCost} disabled={savingCost || !costForm?.monto || (costFueraTarifaWarning && !confirmCostFueraTarifa)} data-testid="load-cost-dialog-save">
              {savingCost ? "Guardando..." : "Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark collected dialog */}
      <Dialog open={collectOpen} onOpenChange={setCollectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Marcar cobrado — {load.numero_load}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Fecha de cobro *</Label>
              <Input type="date" className="mt-1" value={collectForm.fecha_cobro} onChange={(e) => setCollectForm({ ...collectForm, fecha_cobro: e.target.value })} data-testid="load-collect-fecha" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Referencia (transferencia, cheque o depósito) *</Label>
              <Input className="mt-1" value={collectForm.referencia_cobro} onChange={(e) => setCollectForm({ ...collectForm, referencia_cobro: e.target.value })} data-testid="load-collect-referencia" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollectOpen(false)}>Cancelar</Button>
            <Button onClick={doCollect} disabled={collecting || !collectForm.fecha_cobro || !collectForm.referencia_cobro} data-testid="load-collect-confirm-btn">
              {collecting ? "Guardando..." : "Marcar cobrado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark cost paid dialog */}
      <Dialog open={!!payCostId} onOpenChange={(o) => { if (!o) setPayCostId(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Marcar pagado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Fecha de pago *</Label>
              <Input type="date" className="mt-1" value={payForm.fecha_pago} onChange={(e) => setPayForm({ ...payForm, fecha_pago: e.target.value })} data-testid="load-pay-fecha" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Referencia de pago *</Label>
              <Input className="mt-1" value={payForm.referencia_pago} onChange={(e) => setPayForm({ ...payForm, referencia_pago: e.target.value })} data-testid="load-pay-referencia" />
            </div>
            {payWarning && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="load-pay-duplicado-warning">
                <div className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle className="h-4 w-4" /> Posible pago duplicado</div>
                <p className="mt-1 text-sm text-amber-800">{payWarning.message}</p>
                <label className="mt-2 flex items-center gap-2 text-sm text-amber-800">
                  <input type="checkbox" checked={payForm.confirmar_pago_duplicado} onChange={(e) => setPayForm({ ...payForm, confirmar_pago_duplicado: e.target.checked })} data-testid="load-pay-confirm-duplicado" />
                  Confirmo que es un pago distinto
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayCostId(null)}>Cancelar</Button>
            <Button onClick={doPay} disabled={paying || !payForm.fecha_pago || !payForm.referencia_pago || (payWarning && !payForm.confirmar_pago_duplicado)} data-testid="load-pay-confirm-btn">
              {paying ? "Guardando..." : "Marcar pagado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock dialog */}
      <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Desbloquear load {load.numero_load}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs text-muted-foreground">Motivo del desbloqueo (mínimo 10 caracteres) *</Label>
            <Textarea className="mt-1" value={unlockMotivo} onChange={(e) => setUnlockMotivo(e.target.value)} data-testid="load-unlock-motivo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockOpen(false)}>Cancelar</Button>
            <Button onClick={doUnlock} disabled={unlocking || unlockMotivo.trim().length < 10} className="bg-amber-600 hover:bg-amber-700" data-testid="load-unlock-confirm-btn">
              {unlocking ? "Desbloqueando..." : "Desbloquear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invalidate dialog */}
      <Dialog open={invalidateOpen} onOpenChange={setInvalidateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Invalidar load {load.numero_load}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Motivo (mínimo 10 caracteres) *</Label>
              <Textarea className="mt-1" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="load-invalidate-motivo" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={generarReemplazo} onChange={(e) => setGenerarReemplazo(e.target.checked)} data-testid="load-invalidate-generar-reemplazo" />
              Generar load de reemplazo automáticamente
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvalidateOpen(false)}>Cancelar</Button>
            <Button onClick={doInvalidate} disabled={invalidating || motivo.trim().length < 10} className="bg-red-600 hover:bg-red-700" data-testid="load-invalidate-confirm-btn">
              {invalidating ? "Invalidando..." : "Invalidar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anular cost dialog */}
      <Dialog open={!!anularCostId} onOpenChange={(o) => { if (!o) setAnularCostId(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Anular costo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">El costo quedará marcado como anulado, visible en el historial pero sin sumar en ningún total.</p>
            <div>
              <Label className="text-xs text-muted-foreground">Motivo (mínimo 10 caracteres) *</Label>
              <Textarea className="mt-1" value={anularMotivo} onChange={(e) => setAnularMotivo(e.target.value)} data-testid="load-anular-cost-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnularCostId(null)}>Cancelar</Button>
            <Button onClick={doAnularCost} disabled={anulando || anularMotivo.trim().length < 10} className="bg-red-600 hover:bg-red-700" data-testid="load-anular-cost-confirm-btn">
              {anulando ? "Anulando..." : "Anular costo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revertir pago dialog */}
      <Dialog open={!!revertPagoCostId} onOpenChange={(o) => { if (!o) setRevertPagoCostId(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Revertir pago</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">El pago anterior quedará registrado en el historial del costo, no se borra.</p>
            <div>
              <Label className="text-xs text-muted-foreground">Motivo (mínimo 10 caracteres) *</Label>
              <Textarea className="mt-1" value={revertPagoMotivo} onChange={(e) => setRevertPagoMotivo(e.target.value)} data-testid="load-revert-pago-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevertPagoCostId(null)}>Cancelar</Button>
            <Button onClick={doRevertirPago} disabled={revertingPago || revertPagoMotivo.trim().length < 10} className="bg-amber-600 hover:bg-amber-700" data-testid="load-revert-pago-confirm-btn">
              {revertingPago ? "Revirtiendo..." : "Revertir pago"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revertir cobro dialog */}
      <Dialog open={revertCobroOpen} onOpenChange={setRevertCobroOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Revertir cobro</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">El cobro anterior quedará registrado en el historial del load, no se borra.</p>
            <div>
              <Label className="text-xs text-muted-foreground">Motivo (mínimo 10 caracteres) *</Label>
              <Textarea className="mt-1" value={revertCobroMotivo} onChange={(e) => setRevertCobroMotivo(e.target.value)} data-testid="load-revert-cobro-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevertCobroOpen(false)}>Cancelar</Button>
            <Button onClick={doRevertirCobro} disabled={revertingCobro || revertCobroMotivo.trim().length < 10} className="bg-amber-600 hover:bg-amber-700" data-testid="load-revert-cobro-confirm-btn">
              {revertingCobro ? "Revirtiendo..." : "Revertir cobro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, mono = false, className = "" }) {
  return (
    <div className={className}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={mono ? "font-mono text-sm" : "text-sm"}>{value}</p>
    </div>
  );
}
